import React, { useEffect, useRef, useState } from "react";

// Styling
import { makeStyles } from "@material-ui/core/styles";

// Components
import Button from "@material-ui/core/Button";
import MaterialTable from "material-table";
import SectionTitle from "../../Sales/Orders/SectionTitle";
import EditableTableCell from "../../Table/EditableTableCell";
import RatesChangePreviewDialog from "../../Sales/Orders/RatesChangePreviewDialog";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import MuiAlert from "@material-ui/lab/Alert";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import NumberFormatCustom from "../NumberFormatCustom";
import IntegerInput from "../IntegerInput";

// Icons
import tableIcons from "../../Table/TableIcons";

const useStyles = makeStyles((theme) => ({
  alert: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
  resize: {
    fontSize: theme.typography.caption.fontSize,
  },
}));

const FREQUENCY_LOOKUP = {
  "Daily/Person": "Daily/Person",
  "One-Time/Person": "One-Time/Person",
  "Daily/In-Room": "Daily/In-Room",
  "One-Time/In-Room": "One-Time/In-Room",
};

const APPLIES_TO_LOOKUP = {
  Babies: "Babies",
  Children: "Children",
  Adults: "Adults",
  Seniors: "Seniors",
  All: "All",
  "All/No Babies": "All/No Babies",
};

const PRODUCT_TYPE_LOOKUP = {
  addon: "Addon",
  upgrade: "Upgrade",
};

const NUMBER_FIELDS = ["price", "tax", "quantity"];

// Need to spread array so it doesn't fail
const formatData = (data) => {
  return data.map((o) => ({
    ...o,
  }));
};

const withoutTableData = ({ tableData, ...row }) => row;

const ProductsTableEdit = ({
  originalData,
  currentAmounts,
  onPreview,
  onSubmit,
  headerAction,
}) => {
  //Styles
  const classes = useStyles();

  // Local State
  const [data, setData] = useState(formatData(originalData));
  const [alert, setAlert] = useState({
    open: false,
    content: "",
  });
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewAmounts, setPreviewAmounts] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validateData = (newData) => {
    const { name, price, tax, quantity } = newData;
    const hasPriceValue =
      price !== undefined && price !== null && String(price).trim() !== "";
    const hasTaxValue =
      tax !== undefined && tax !== null && String(tax).trim() !== "";

    if (!name || String(name).trim() === "") {
      return { valid: false, content: "Name cannot be empty!" };
    }
    if (!hasPriceValue || isNaN(Number(price))) {
      return { valid: false, content: "Price needs to be a valid number!" };
    }
    if (Number(price) < 0) {
      return { valid: false, content: "Price cannot be negative!" };
    }
    if (!hasTaxValue || isNaN(Number(tax))) {
      return { valid: false, content: "Tax needs to be a valid number!" };
    }
    if (Number(tax) < 0 || Number(tax) > 100) {
      return { valid: false, content: "Tax must be between 0 and 100!" };
    }
    if (!Number.isInteger(Number(quantity)) || Number(quantity) <= 0) {
      return {
        valid: false,
        content: "Quantity needs to be a whole number larger than 0!",
      };
    }

    return { valid: true, content: "" };
  };

  const clearAlert = () => setAlert({ open: false, content: "" });

  const formatProducts = (products) =>
    products.map(withoutTableData).map((product) => ({
      ...product,
      price: Number(product.price),
      tax: Number(product.tax),
      quantity: Number(product.quantity),
    }));

  const handlePreview = async () => {
    setSubmitError(false);
    setIsPreviewing(true);

    const res = await onPreview(formatProducts(data));

    if (!isMountedRef.current) return;
    setIsPreviewing(false);

    if (res.error) {
      setAlert({
        open: true,
        content:
          res.errorMessage ||
          "There was an error previewing the new pricing. Please try again.",
      });
      return;
    }

    clearAlert();
    setPreviewAmounts(res.amounts);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setSubmitError(false);

    const succeeded = await onSubmit(formatProducts(data));

    // On success the parent leaves edit mode and unmounts this table.
    if (!isMountedRef.current) return;

    setIsSubmitting(false);
    if (succeeded === false) {
      setSubmitError(true);
    } else {
      setPreviewAmounts(null);
    }
  };

  return (
    <Grid container item spacing={2}>
      {alert.open && (
        <Grid item xs={12}>
          <MuiAlert
            className={classes.alert}
            onClose={clearAlert}
            severity="error"
          >
            {alert.content}
          </MuiAlert>
        </Grid>
      )}
      <Grid item xs={12}>
        <MaterialTable
          title={
            <div>
              <SectionTitle title="Products" action={headerAction} />
              <Typography variant="caption" color="textSecondary">
                Click a value to edit it. Enter saves, Esc cancels.
              </Typography>
            </div>
          }
          icons={tableIcons}
          components={{
            Cell: EditableTableCell,
          }}
          columns={[
            {
              title: "Name",
              field: "name",
              initialEditValue: "Product",
              editComponent: (props) => (
                <TextField
                  value={props.value}
                  onChange={(e) => props.onChange(e.target.value)}
                  InputProps={{
                    classes: {
                      input: classes.resize,
                    },
                  }}
                />
              ),
            },
            {
              title: "Price",
              field: "price",
              type: "currency",
              headerStyle: {
                textAlign: "right",
              },
              initialEditValue: 1,
              editComponent: (props) => (
                <TextField
                  value={props.value}
                  onChange={(e) => props.onChange(e.target.value)}
                  inputProps={{
                    thousandSeparator: true,
                  }}
                  InputProps={{
                    inputComponent: NumberFormatCustom,
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    classes: {
                      input: classes.resize,
                    },
                  }}
                />
              ),
            },
            {
              title: "Tax",
              field: "tax",
              type: "numeric",
              render: (rowData) => <>{rowData.tax}%</>,
              initialEditValue: 1,
              editComponent: (props) => (
                <TextField
                  value={props.value}
                  onChange={(e) => props.onChange(e.target.value)}
                  inputProps={{
                    isAllowed: (values) => {
                      const { formattedValue, floatValue } = values;
                      return formattedValue === "" || floatValue <= 100;
                    },
                  }}
                  InputProps={{
                    classes: {
                      input: classes.resize,
                    },
                    inputComponent: NumberFormatCustom,
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                  }}
                />
              ),
            },
            {
              title: "Quantity",
              field: "quantity",
              type: "numeric",
              initialEditValue: 1,
              editComponent: (props) => (
                <TextField
                  value={props.value}
                  onChange={(e) => props.onChange(e.target.value)}
                  InputProps={{
                    inputComponent: IntegerInput,
                    classes: {
                      input: classes.resize,
                    },
                  }}
                />
              ),
            },
            {
              title: "Frequency",
              field: "type",
              initialEditValue: "Daily/Person",
              lookup: FREQUENCY_LOOKUP,
            },
            {
              title: "Applies To",
              field: "age_restriction",
              initialEditValue: "All",
              lookup: APPLIES_TO_LOOKUP,
            },
            {
              title: "Product Type",
              field: "product_type",
              initialEditValue: "addon",
              lookup: PRODUCT_TYPE_LOOKUP,
            },
          ]}
          data={data}
          options={{
            search: false,
            paging: false,
            actionsColumnIndex: -1,
          }}
          localization={{
            body: {
              emptyDataSourceMessage: "No products. Use + to add one.",
            },
          }}
          cellEditable={{
            onCellEditApproved: (newValue, oldValue, rowData, columnDef) =>
              new Promise((resolve, reject) => {
                const index = rowData.tableData.id;
                const field = columnDef.field;
                const updatedRow = {
                  ...withoutTableData(data[index]),
                  [field]: NUMBER_FIELDS.includes(field)
                    ? Number(newValue)
                    : newValue,
                };
                const validateObj = validateData({
                  ...updatedRow,
                  // Keep the raw value so empty input is caught
                  [field]: newValue,
                });

                if (!validateObj.valid) {
                  setAlert({ open: true, content: validateObj.content });
                  reject();
                  return;
                }

                const dataUpdate = [...data];
                dataUpdate[index] = updatedRow;
                setData(dataUpdate);
                clearAlert();
                resolve();
              }),
          }}
          editable={{
            onRowAddCancelled: clearAlert,
            onRowAdd: (newData) =>
              new Promise((resolve, reject) => {
                const validateObj = validateData(newData);
                if (validateObj.valid) {
                  setData([...data, newData]);
                  clearAlert();
                  resolve();
                } else {
                  setAlert({
                    open: true,
                    content: validateObj.content,
                  });
                  reject();
                }
              }),
            onRowDelete: (oldData) =>
              new Promise((resolve) => {
                const dataDelete = [...data];
                dataDelete.splice(oldData.tableData.id, 1);
                setData(dataDelete);
                resolve();
              }),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Box mb={3} clone>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            size="large"
            disabled={isPreviewing}
            startIcon={
              isPreviewing ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            onClick={handlePreview}
          >
            {isPreviewing ? "Calculating…" : "Preview New Pricing"}
          </Button>
        </Box>
      </Grid>

      {previewAmounts && (
        <RatesChangePreviewDialog
          open
          onClose={() => setPreviewAmounts(null)}
          onConfirm={handleConfirm}
          isSubmitting={isSubmitting}
          submitError={submitError}
          amounts={currentAmounts || {}}
          nextAmounts={previewAmounts}
          currentProducts={originalData}
          nextProducts={formatProducts(data)}
        />
      )}
    </Grid>
  );
};

export default ProductsTableEdit;
